import type { Rv2Lang, Rv2Messages, Rv2Vars } from "./types";
import { frMessages, enMessages, arMessages } from "./messages/bundle";
import type { Rv2Key } from "./messages/keys";

/** Backward-compatible flat keys from the original translations.ts */
const LEGACY_KEY_MAP: Record<string, Rv2Key> = {
  getStarted: "nav.getStarted",
  getDemo: "shared.cta.getDemo",
  fromPrice: "landing.hero.fromPrice",
  perMonth: "landing.hero.perMonth",
  instantSignup: "landing.hero.bullet.instantSignup",
  viaWhatsapp: "landing.hero.bullet.viaWhatsapp",
  poweredByAi: "landing.hero.bullet.poweredByAi",
  adoptedBy: "landing.hero.badge",
  heroTitle1: "landing.hero.title1",
  heroTitle2: "landing.hero.title2",
  heroTitle3: "landing.hero.title3",
  heroDesc: "landing.hero.desc",
  howTitle: "landing.how.title",
  howSubtitle: "landing.how.subtitle",
  pricingTitle: "pricing.title",
  pricingSubtitle: "pricing.subtitle",
  finalCtaTitle: "landing.finalCta.title",
  finalCtaSubtitle: "landing.finalCta.subtitle",
  callAdvisor: "shared.cta.callAdvisor",
  commercantsBadge: "commercants.badge",
  commercantsTitle: "commercants.title",
  commercantsSubtitle: "commercants.subtitle",
  reserveDemo: "commercants.demo.title",
  confirmDemo: "commercants.form.submit",
  requestReceived: "commercants.confirmation.title",
  checkoutBilling: "checkout.billing.title",
  checkoutPlan: "checkout.plan.title",
  checkoutPayment: "checkout.steps.payment",
  createAccess: "creationLogin.title",
  accessBackoffice: "creationLogin.submit.accessBackoffice",
  dashboard: "backoffice.sidebar.dashboard",
  welcomeBackoffice: "backoffice.welcome.title",
  configureProgram: "backoffice.welcome.cta",
  product: "shared.footer.product",
  company: "shared.footer.company",
  contact: "shared.footer.contact",
  footerTagline: "shared.footer.tagline",
  rights: "shared.footer.rights",
  legal: "shared.footer.legal",
  privacy: "shared.footer.privacy",
  cgv: "shared.footer.cgv",
  securePayment: "shared.securePayment",
};

const bundles: Record<Rv2Lang, Rv2Messages> = {
  fr: frMessages,
  en: enMessages,
  ar: arMessages,
};

function resolveKey(key: string): Rv2Key {
  return (LEGACY_KEY_MAP[key] ?? key) as Rv2Key;
}

function interpolate(template: string, vars?: Rv2Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, name: string) => {
    const val = vars[name];
    return val === undefined ? `{${name}}` : String(val);
  });
}

export function rv2T(lang: string, key: string, vars?: Rv2Vars): string {
  const l = (["fr", "en", "ar"].includes(lang) ? lang : "fr") as Rv2Lang;
  const resolved = resolveKey(key);
  const dict = bundles[l];
  const fr = bundles.fr;
  const raw = dict[resolved] ?? fr[resolved];

  if (raw === undefined) {
    if (process.env.NODE_ENV === "development") {
      console.warn(`[rv2 i18n] missing key: ${resolved}`);
    }
    return resolved;
  }

  let out = interpolate(raw, vars);

  if (l === "ar" && (out.includes("→") || out.includes("←"))) {
    out = out.split("→").join("\u0001").split("←").join("→").split("\u0001").join("←");
  }

  return out;
}

export function getRv2Messages(lang: Rv2Lang): Rv2Messages {
  return bundles[lang];
}
